import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { type TradeModuleStore, useTradeModuleStore } from '@/components/TransactionModule/store';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { ChainAddress } from '@/utils/models';
import { isArcUsdcPair } from '@/utils/multichain';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';
import { getDefaultPairForChain, getDefaultTradeParams } from '@/utils/swap';

type SyncUrlParameters = (
  buyAddress?: ChainAddress,
  buyChain?: number,
  sellAddress?: ChainAddress,
  sellChain?: number,
) => void;

type UseInitializeDefaultTokensParams = {
  buyTokenAddress: ChainAddress | undefined;
  buyTokenChainId: number | undefined;
  sellTokenAddress: ChainAddress | undefined;
  sellTokenChainId: number | undefined;
  /**
   * Writes the resolved token query parameters to the URL. Each consumer
   * decides how to render them (e.g. token pages ignore the buy side because it
   * is the page token).
   */
  syncUrlParameters: SyncUrlParameters;
  /**
   * Wallet-follow trade surfaces only: while the user has not touched the
   * form, the pair follows the connected wallet's chain (re-defaults on wallet
   * connect/chain switch). Token pages keep their fixed chain, so they pass
   * `false`. Captured once in the first effect with a ready router.
   */
  followWalletChain: boolean;
};

const hasTradeAmountSelector = (state: TradeModuleStore) =>
  !!state.sellInput.decimal || !!state.buyInput.decimal;

/** Chain-aware address equality (EVM is case-insensitive, Solana is not). */
const addressesEqual = (
  a: ChainAddress | undefined,
  b: ChainAddress | undefined,
  chainId: number,
): boolean =>
  a != null && b != null && normalizeTokenAddress(a, chainId) === normalizeTokenAddress(b, chainId);

/** True when the current pair is empty or exactly the default pair on its chain. */
const isAutoOrEmptyPair = (
  buyTokenAddress: ChainAddress | undefined,
  buyTokenChainId: number | undefined,
  sellTokenAddress: ChainAddress | undefined,
  sellTokenChainId: number | undefined,
): boolean => {
  if (buyTokenAddress == null && sellTokenAddress == null) return true;
  if (buyTokenAddress == null || sellTokenAddress == null) return false;
  if (buyTokenChainId !== sellTokenChainId || buyTokenChainId == null) return false;

  const { buyAddress, sellAddress } = getDefaultPairForChain(buyTokenChainId);
  return (
    addressesEqual(buyTokenAddress, buyAddress, buyTokenChainId) &&
    addressesEqual(sellTokenAddress, sellAddress, buyTokenChainId)
  );
};

/**
 * Ensures the trade module never renders with an empty token field by filling
 * the empty side(s) with the static default pair (see ADR 0001). Wallet
 * balances never influence the selection.
 *
 * - Exactly one side empty (token pages, deep links): fill the empty side on
 *   the present side's chain, flipping to the counterpart's default on
 *   collision (e.g. a USDC page becomes native -> USDC).
 * - Both sides empty (wallet-follow trade surfaces): wait for wallet restore to
 *   settle, then fill with the default pair on the wallet's chain. While the
 *   user has not touched the form, the pair re-defaults on connect/chain switch.
 *
 * Returns `setShouldInitialize` so callers can freeze re-defaulting when the
 * user selects or flips a token.
 */
export const useInitializeDefaultTokens = ({
  buyTokenAddress,
  buyTokenChainId,
  sellTokenAddress,
  sellTokenChainId,
  syncUrlParameters,
  followWalletChain,
}: UseInitializeDefaultTokensParams) => {
  const router = useRouter();
  const { ethereumWallet, solanaWallet, walletsReady } = useMatchaWallets();

  const [shouldInitialize, setShouldInitialize] = useState(true);
  const followWalletChainRef = useRef<boolean | undefined>(undefined);

  // An amount in the form suspends wallet-follow (checked live in the guard
  // below) so a static token never swaps under an amount the user has already
  // typed. A per-mount freeze latch would race a same-commit amount+wallet
  // change; reading the store each run cannot. An empty side is still filled
  // regardless - the fill branch precedes the guard.
  const hasTradeAmount = useTradeModuleStore(hasTradeAmountSelector);
  const hasEmptySide = buyTokenAddress == null || sellTokenAddress == null;

  // Re-arm whenever a side becomes empty again (e.g. navigating to a new token
  // page) so the module is never left with an empty field.
  useEffect(() => {
    if (hasEmptySide) setShouldInitialize(true);
  }, [hasEmptySide]);

  useEffect(() => {
    if (!router.isReady) return;

    // Capture the entry decision before any default fill changes the URL.
    // Effect-time capture cannot leak from a discarded render.
    if (followWalletChainRef.current === undefined) {
      followWalletChainRef.current = followWalletChain;
    }

    const buyEmpty = buyTokenAddress == null;
    const sellEmpty = sellTokenAddress == null;

    // Exactly one side present: fill the empty side on the present chain.
    if (buyEmpty !== sellEmpty) {
      // The present side always has its chainId set alongside its address; bail
      // rather than resolving defaults against an undefined chain.
      const presentChainId = buyEmpty ? sellTokenChainId : buyTokenChainId;
      if (presentChainId == null) return;

      const { buyAddress, sellAddress } = getDefaultPairForChain(presentChainId);

      // A one-sided URL is an explicit choice, so the filled pair must not be
      // re-defaulted to the wallet chain on the next run.
      followWalletChainRef.current = false;

      if (buyEmpty) {
        // Flip to the sell default if the native buy would collide with the sell.
        const nextBuy = addressesEqual(sellTokenAddress, buyAddress, presentChainId)
          ? sellAddress
          : buyAddress;
        syncUrlParameters(nextBuy, presentChainId, sellTokenAddress, sellTokenChainId);
      } else {
        // Flip to the buy default if the USDC sell would collide with the buy.
        const nextSell =
          addressesEqual(buyTokenAddress, sellAddress, presentChainId) ||
          isArcUsdcPair(buyTokenAddress, sellAddress, presentChainId)
            ? buyAddress
            : sellAddress;
        syncUrlParameters(buyTokenAddress, buyTokenChainId, nextSell, presentChainId);
      }
      return;
    }

    // Both empty or both present: only an untouched default pair on a
    // wallet-follow surface re-defaults to follow the wallet chain.
    if (
      !followWalletChainRef.current ||
      !walletsReady ||
      !shouldInitialize ||
      hasTradeAmount ||
      !isAutoOrEmptyPair(buyTokenAddress, buyTokenChainId, sellTokenAddress, sellTokenChainId)
    ) {
      return;
    }

    const defaults = getDefaultTradeParams(ethereumWallet, solanaWallet);
    const targetBuyChainId = Number(defaults.buyChain);
    const targetSellChainId = Number(defaults.sellChain);

    const changed =
      !addressesEqual(defaults.buyAddress, buyTokenAddress, targetBuyChainId) ||
      targetBuyChainId !== buyTokenChainId ||
      !addressesEqual(defaults.sellAddress, sellTokenAddress, targetSellChainId) ||
      targetSellChainId !== sellTokenChainId;

    if (changed) {
      syncUrlParameters(
        defaults.buyAddress,
        targetBuyChainId,
        defaults.sellAddress,
        targetSellChainId,
      );
    }
  }, [
    router.isReady,
    followWalletChain,
    walletsReady,
    shouldInitialize,
    hasTradeAmount,
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    ethereumWallet,
    solanaWallet,
    syncUrlParameters,
  ]);

  return { setShouldInitialize };
};
